import { FormEvent, useCallback } from 'react';
import { useParams } from 'react-router';
import { CONTRACT_TYPE, EXPIRY_METHOD, STRIKE_METHOD } from '@voladeum/client';
import { useClientContext } from 'client/context';
import { Strategy } from 'client/voladeum/strategies/types';
import { selectContractTypes } from 'ducks/contract-types/selectors';
import { selectExpiryMethods } from 'ducks/expiry-methods/selectors';
import { selectPopover } from 'ducks/popovers/selectors';
import { clearPopover } from 'ducks/popovers/slices';
import { updateSettings } from 'ducks/settings/slices';
import { selectSizingMethods } from 'ducks/sizing-methods/selectors';
import { setStrategy } from 'ducks/strategy/slices';
import { selectStrikeMethods } from 'ducks/strike-methods/selectors';
import { Button } from 'framework/button';
import { Dropdown, DropdownCalendar } from 'framework/dropdown';
import { Input, InputNumber, InputPercent } from 'framework/Input';
import { Popover } from 'framework/popover';
import moment from 'moment';
import { useStrategyContext } from 'pages/context/StrategyContext';
import { useStrategyApi } from 'pages/hooks/useStrategyApi';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import {
  ACTIVATION_THRESHOLD,
  MAX_DAILY_LOSS_PCT,
  MAX_DRAWDOWN_PCT,
  MAX_POSITION_PCT,
  SLIPPAGE_TOLERANCE_PCT,
  STOP_LOSS_OFFSET_PCT,
  TAKE_PROFIT_OFFSET_PCT,
  TAKE_PROFIT_TRAIL_PCT,
  THEME,
} from '../../../constants';

import cn from '../Popover.module.scss';
export const ManageStrategyPopover = () => {
  const dispatch = useAppDispatch();
  const { strategy_id } = useParams();
  const voladeum_contract_types = useAppSelector(selectContractTypes);
  const voladeum_expiry_methods = useAppSelector(selectExpiryMethods);
  const voladeum_sizing_methods = useAppSelector(selectSizingMethods);
  const voladeum_strike_methods = useAppSelector(selectStrikeMethods);

  const { is_open } = useAppSelector(selectPopover('strategy_manage'));
  const { strategy, updateStrategy } = useStrategyContext();
  const { onStrategySubmit } = useStrategyApi();
  const { Voladeum, handleError } = useClientContext();

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      try {
        await onStrategySubmit(e);
        const voladeum_strategy = await Voladeum.strategies.fetch(strategy_id);
        dispatch(setStrategy(voladeum_strategy.data));
        dispatch(clearPopover('strategy_manage'));
        dispatch(updateSettings({ loading: false }));
      } catch (err) {
        handleError(err);
      }
    },
    [Voladeum.strategies, dispatch, handleError, onStrategySubmit, strategy_id]
  );

  const onClose = useCallback(() => {
    dispatch(clearPopover('strategy_manage'));
  }, [dispatch]);

  const onUpdate = useCallback(
    (entity: Partial<Strategy>) => updateStrategy(entity),
    [updateStrategy]
  );

  const {
    strategy_name,
    sizing_method_name,
    expiry_method_id,
    expiry_method_name,
    strike_method_name,
    strike_method_id,
    contract_type_name,
    strike_value_formatted,
    expiry_value_formatted,
    slippage_tolerance_pct_formatted,
    stop_loss_offset_pct_formatted,
    take_profit_offset_pct_formatted,
    take_profit_trail_pct_formatted,
    max_daily_loss_pct_formatted,
    max_drawdown_pct_formatted,
    max_position_pct_formatted,
    activation_threshold_formatted,
    to_utc_formatted,
    contract_type_id,
  } = strategy;

  return (
    <Popover
      identifier="strategy_manage"
      right="366px"
      onClose={onClose}
      onSubmit={onSubmit}
      width="fit-content"
      is_open={is_open}
    >
      <Popover.Header />
      <Popover.Body>
        <p>Strategy</p>
        <div className={cn.spread}>
          <p className={cn.label}>Name *</p>
          <Input
            value={strategy_name}
            height="26px"
            width="175px"
            theme={THEME.input.slim}
            onChange={({ value }) => {
              onUpdate({ strategy_name: value });
            }}
          />
          <p className={cn.label}>Contract *</p>
          <Dropdown
            options={voladeum_contract_types}
            option_key="contract_type_name"
            height="26px"
            width="175px"
            theme={THEME.select.slim}
            value={contract_type_name}
            onSelect={({ contract_type_id, contract_type_name }) =>
              onUpdate({ contract_type_id, contract_type_name })
            }
          />
          {CONTRACT_TYPE.OPTION === contract_type_id && (
            <>
              <p className={cn.label}>Strike Method *</p>
              <Dropdown
                options={voladeum_strike_methods}
                option_key="strike_method_name"
                height="26px"
                width="175px"
                theme={THEME.select.slim}
                value={strike_method_name || ''}
                onSelect={({ strike_method_id, strike_method_name }) =>
                  onUpdate({ strike_method_id, strike_method_name })
                }
              />
              {STRIKE_METHOD.PERCENT_OTM === strike_method_id && (
                <>
                  <p className={cn.label}>Strike Value</p>
                  <InputPercent
                    value={strike_value_formatted}
                    height="26px"
                    width="175px"
                    theme={THEME.input.slim}
                    onChange={({ float_value, formatted_value }) => {
                      onUpdate({
                        strike_value: float_value,
                        strike_value_formatted: formatted_value,
                      });
                    }}
                  />
                </>
              )}
              <p className={cn.label}>Expiry Method *</p>
              <Dropdown
                options={voladeum_expiry_methods}
                option_key="expiry_method_name"
                height="26px"
                width="175px"
                theme={THEME.select.slim}
                value={expiry_method_name || ''}
                onSelect={({ expiry_method_id, expiry_method_name }) =>
                  onUpdate({ expiry_method_id, expiry_method_name })
                }
              />
              {EXPIRY_METHOD.DAYS_AHEAD === expiry_method_id && (
                <>
                  <p className={cn.label}>Expiry Value</p>
                  <InputNumber
                    value={expiry_value_formatted}
                    height="26px"
                    width="175px"
                    theme={THEME.input.slim}
                    onChange={({ float_value, formatted_value }) => {
                      onUpdate({
                        expiry_value: float_value,
                        expiry_value_formatted: formatted_value,
                      });
                    }}
                  />
                </>
              )}
            </>
          )}
          <p className={cn.label}>End Date (Backtest) *</p>
          <DropdownCalendar
            information="Lookback 1 day when building the strategy and 30 days when backtesting."
            height="26px"
            width="175px"
            theme={THEME.select.slim}
            value={[to_utc_formatted]}
            onSelect={([date]) => {
              const since_milliseconds = moment.utc(date, 'MM/DD/YYYY').startOf('day').valueOf();
              const to_milliseconds = moment.utc(date, 'MM/DD/YYYY').endOf('day').valueOf();
              onUpdate({
                since_utc_formatted: date,
                since_milliseconds: since_milliseconds,
                to_utc_formatted: date,
                to_milliseconds: to_milliseconds,
              });
            }}
          />
        </div>
        <p>Risk</p>
        <div className={cn.spread}>
          <p className={cn.label}>Sizing Method *</p>
          <Dropdown
            options={voladeum_sizing_methods}
            option_key="sizing_method_name"
            height="26px"
            width="175px"
            theme={THEME.select.slim}
            value={sizing_method_name}
            onSelect={({ sizing_method_id, sizing_method_name }) =>
              onUpdate({ sizing_method_id, sizing_method_name })
            }
          />
          <p className={cn.label}>Max Position *</p>
          <InputPercent
            value={max_position_pct_formatted}
            placeholder={MAX_POSITION_PCT}
            height="26px"
            width="175px"
            theme={THEME.input.slim}
            onChange={({ float_value, formatted_value }) => {
              onUpdate({
                max_position_pct: float_value,
                max_position_pct_formatted: formatted_value,
              });
            }}
          />
          <p className={cn.label}>Max Daily Loss *</p>
          <InputPercent
            value={max_daily_loss_pct_formatted}
            placeholder={MAX_DAILY_LOSS_PCT}
            height="26px"
            width="175px"
            theme={THEME.input.slim}
            onChange={({ float_value, formatted_value }) => {
              onUpdate({
                max_daily_loss_pct: float_value,
                max_daily_loss_pct_formatted: formatted_value,
              });
            }}
          />
          <p className={cn.label}>Max Drawdown *</p>
          <InputPercent
            value={max_drawdown_pct_formatted}
            placeholder={MAX_DRAWDOWN_PCT}
            height="26px"
            width="175px"
            theme={THEME.input.slim}
            onChange={({ float_value, formatted_value }) => {
              onUpdate({
                max_drawdown_pct: float_value,
                max_drawdown_pct_formatted: formatted_value,
              });
            }}
          />
        </div>
        <p>Settings</p>
        <div className={cn.spread}>
          <p className={cn.label}>Slippage Tolerance *</p>
          <InputPercent
            value={slippage_tolerance_pct_formatted}
            placeholder={SLIPPAGE_TOLERANCE_PCT}
            height="26px"
            width="175px"
            theme={THEME.input.slim}
            onChange={({ float_value, formatted_value }) => {
              onUpdate({
                slippage_tolerance_pct: float_value,
                slippage_tolerance_pct_formatted: formatted_value,
              });
            }}
          />
          <p className={cn.label}>Stop Loss *</p>
          <InputPercent
            value={stop_loss_offset_pct_formatted}
            placeholder={STOP_LOSS_OFFSET_PCT}
            height="26px"
            width="175px"
            theme={THEME.input.slim}
            onChange={({ float_value, formatted_value }) => {
              onUpdate({
                stop_loss_offset_pct: float_value,
                stop_loss_offset_pct_formatted: formatted_value,
              });
            }}
          />
          <p className={cn.label}>Take Profit *</p>
          <InputPercent
            value={take_profit_offset_pct_formatted}
            placeholder={TAKE_PROFIT_OFFSET_PCT}
            height="26px"
            width="175px"
            theme={THEME.input.slim}
            onChange={({ float_value, formatted_value }) => {
              onUpdate({
                take_profit_offset_pct: float_value,
                take_profit_offset_pct_formatted: formatted_value,
              });
            }}
          />
          <p className={cn.label}>Take Profit Trail *</p>
          <InputPercent
            value={take_profit_trail_pct_formatted}
            placeholder={TAKE_PROFIT_TRAIL_PCT}
            height="26px"
            width="175px"
            theme={THEME.input.slim}
            onChange={({ float_value, formatted_value }) => {
              onUpdate({
                take_profit_trail_pct: float_value,
                take_profit_trail_pct_formatted: formatted_value,
              });
            }}
          />
          <p className={cn.label}>Threshold *</p>
          <InputNumber
            information="minimum threshold to trigger an order"
            placeholder={ACTIVATION_THRESHOLD}
            value={activation_threshold_formatted}
            height="26px"
            width="175px"
            theme={THEME.input.slim}
            onChange={({ float_value, formatted_value }) =>
              onUpdate({
                activation_threshold: float_value,
                activation_threshold_formatted: formatted_value,
              })
            }
          />
        </div>
      </Popover.Body>
      <Popover.Footer>
        <Button theme={THEME.button.slim} variant="transparent" height="28px">
          Cancel
        </Button>
        <Button theme={THEME.button.slim} type="submit" height="28px">
          Ok
        </Button>
      </Popover.Footer>
    </Popover>
  );
};
